import { useEffect, useState } from "react";

const MONTHS = [
  "Janvier", "Février", "Mars", "Avril", "Mai", "Juin",
  "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre",
];

function daysInMonth(month) {
  if (!month) return 31;
  return new Date(2000, month, 0).getDate();
}

/**
 * Free-typing day + month picker. Value is a MM-DD string (e.g. "05-15") or "" to clear.
 * onChange receives the new MM-DD string, or "" when incomplete/invalid.
 */
export default function MonthDayPicker({ value, onChange, testIdPrefix = "birthday" }) {
  // Local text state so users can type freely (empty, "1", "12", …) without the parent
  // constantly wiping it out until MM and DD are both valid.
  const [dayText, setDayText] = useState("");
  const [monthText, setMonthText] = useState("");

  // Sync from prop when it changes externally (initial load, reset, etc.)
  useEffect(() => {
    if (!value || typeof value !== "string") {
      setDayText("");
      setMonthText("");
      return;
    }
    const md = value.length === 5 ? value : value.length >= 10 ? value.slice(5, 10) : "";
    if (!/^\d{2}-\d{2}$/.test(md)) {
      setDayText("");
      setMonthText("");
      return;
    }
    setMonthText(String(parseInt(md.slice(0, 2), 10)));
    setDayText(String(parseInt(md.slice(3, 5), 10)));
  }, [value]);

  const emitIfValid = (dText, mText) => {
    const d = parseInt(dText, 10);
    const m = parseInt(mText, 10);
    if (!Number.isFinite(d) || !Number.isFinite(m)) {
      onChange("");
      return;
    }
    if (m < 1 || m > 12 || d < 1 || d > daysInMonth(m)) {
      onChange("");
      return;
    }
    onChange(`${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`);
  };

  const sanitize = (s, max) => {
    const digits = (s || "").replace(/\D/g, "").slice(0, 2);
    if (!digits) return "";
    // Allow the intermediate typing of "0" (user typing "05") — cap only if > max
    const n = parseInt(digits, 10);
    if (n > max) return String(max);
    return digits;
  };

  const onDay = (e) => {
    const next = sanitize(e.target.value, 31);
    setDayText(next);
    emitIfValid(next, monthText);
  };

  const onMonth = (e) => {
    const next = sanitize(e.target.value, 12);
    setMonthText(next);
    emitIfValid(dayText, next);
  };

  const clear = () => {
    setDayText("");
    setMonthText("");
    onChange("");
  };

  // Show the month name below the input when it's a valid month, as a helpful hint
  const monthNum = parseInt(monthText, 10);
  const monthHint =
    monthNum >= 1 && monthNum <= 12 ? MONTHS[monthNum - 1] : "";

  return (
    <div>
      <div className="flex items-center gap-2">
        <div className="flex-1">
          <input
            data-testid={`${testIdPrefix}-day`}
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            autoComplete="off"
            maxLength={2}
            placeholder="Jour"
            aria-label="Jour de naissance"
            value={dayText}
            onChange={onDay}
            className="w-full px-3 py-3 bg-slate-800 border border-white/10 rounded-lg focus:border-[#E5B85C] focus:outline-none focus:ring-2 focus:ring-[#E5B85C]/20 text-white font-mono-stat text-center text-lg"
          />
        </div>
        <div className="flex-1">
          <input
            data-testid={`${testIdPrefix}-month`}
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            autoComplete="off"
            maxLength={2}
            placeholder="Mois"
            aria-label="Mois de naissance"
            value={monthText}
            onChange={onMonth}
            className="w-full px-3 py-3 bg-slate-800 border border-white/10 rounded-lg focus:border-[#E5B85C] focus:outline-none focus:ring-2 focus:ring-[#E5B85C]/20 text-white font-mono-stat text-center text-lg"
          />
        </div>
        {(dayText || monthText) && (
          <button
            type="button"
            data-testid={`${testIdPrefix}-clear`}
            onClick={clear}
            title="Effacer"
            aria-label="Effacer"
            className="px-3 py-3 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-400 hover:text-slate-200 text-xs font-mono-stat"
          >
            ✕
          </button>
        )}
      </div>
      {monthHint && (
        <div className="mt-1 text-[11px] text-slate-500 font-mono-stat uppercase tracking-widest">
          {dayText ? `${dayText.padStart(2, "0")} · ` : ""}{monthHint}
        </div>
      )}
    </div>
  );
}

export function formatMonthDay(md) {
  if (!md || typeof md !== "string") return "";
  const s = md.length === 5 ? md : md.length >= 10 ? md.slice(5, 10) : "";
  if (!/^\d{2}-\d{2}$/.test(s)) return "";
  const m = parseInt(s.slice(0, 2), 10);
  const d = parseInt(s.slice(3, 5), 10);
  if (!m || !d) return "";
  return `${String(d).padStart(2, "0")} ${MONTHS[m - 1]}`;
}
